import React, { useCallback, useEffect, useState } from 'react';

import { AccessRequestGetResponse, DecisionStatus } from '@ariksa/jit/api';
import { Box, Center, useDisclosure } from '@chakra-ui/react';
import { each } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { renderTimeStackedCell } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton } from 'components/DataEntry';
import { MonitoringIcon } from 'components/Icons';
import { IconTypes, WithResourceIcon } from 'components/index';
import {
  renderJitApprovers,
  renderJitRequestPermissions,
  renderJitRequestStatus,
} from 'containers/Jit/JitRequests/Components/utils';
import { ViewJITRequest } from 'containers/Jit/JitRequests/Components/ViewJITRequest';

import { selectJitRequests } from '../selectors';
import { actions } from '../slice';

interface MyRequestsTableProps {
  decisionStatus?: DecisionStatus;
}

export const MyRequestsTable = ({
  decisionStatus = undefined,
}: MyRequestsTableProps) => {
  const dispatch = useDispatch();
  const { myRequests } = useSelector(selectJitRequests);
  const requestDisclosure = useDisclosure();
  const [
    selectedRequest,
    setSelectedRequest,
  ] = useState<AccessRequestGetResponse | null>(null);

  const loadRequests = useCallback(() => {
    dispatch(
      actions.getMyRequests({
        q: {
          page: myRequests.page.info.page_number,
          size: myRequests.page.info.page_size,
          decisionStatus,
        },
      }),
    );
  }, [dispatch, myRequests.page.info, decisionStatus]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const columns = [
    {
      header: (
        <Box pl={10} ml={1}>
          Data Group
        </Box>
      ),
      render: ({ row }) => {
        const isTeam = row?.type === 'team';
        return (
          <WithResourceIcon
            resourceType={isTeam ? IconTypes.Group : IconTypes.DataGroup}
            iconFilled={!isTeam}
            color={
              isTeam
                ? customTheme.colors.gray['300']
                : customTheme.colors.blue['300']
            }
            iconStyles={{ boxSize: isTeam ? 5 : 8, ml: isTeam ? 1 : 0 }}
          >
            {isTeam ? row.name : row.data_group_name}
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Permissions',
      render: ({ row }) =>
        row.type === 'team' ? '' : renderJitRequestPermissions(row),
      align: 'left',
    },
    {
      header: 'Received',
      accessor: 'request_creation_time',
      render: ({ value, row }) =>
        row.type === 'team' ? '' : renderTimeStackedCell({ value }),
      align: 'left',
    },
    {
      header: 'Request Start',
      accessor: 'requested_start_time',
      render: ({ value, row }) =>
        row.type === 'team' ? '' : renderTimeStackedCell({ value }),
      align: 'left',
    },
    {
      header: 'Approver(s)',
      align: 'left',
      render: ({ row }) =>
        row.type === 'team' ? '' : renderJitApprovers(row.approvers),
    },
    {
      header: 'Status',
      align: 'left',
      render: ({ row }) =>
        row.type === 'team' ? '' : renderJitRequestStatus({ row }),
    },
    {
      header: 'Actions',
      render: ({ row }) =>
        row.type === 'team' ? (
          ''
        ) : (
          <Center>
            <ActionButton
              label="View request"
              icon={<MonitoringIcon p={0.5} />}
              p={0.5}
              onClick={() => {
                setSelectedRequest(row);
                requestDisclosure.onOpen();
              }}
            />
          </Center>
        ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        data={myRequests.data}
        pagination={{
          totalCount: myRequests.page.totalCount,
          pageInfo: myRequests.page.info,
          onChange: pageInfo =>
            dispatch(actions.updateMyRequestsPagination(pageInfo)),
        }}
        isLoading={myRequests.isLoading}
        isError={myRequests.isError}
        noDataMessage={<Center h="160px">No access requests found</Center>}
      />
      {selectedRequest && (
        <ViewJITRequest
          isOpen={requestDisclosure.isOpen}
          onClose={() => {
            requestDisclosure.onClose();
            setSelectedRequest(null);
          }}
          row={selectedRequest}
          viewBy="requests"
          onActionSuccess={loadRequests}
        />
      )}
    </>
  );
};
